import { AngularGlobals } from "@common/lib/angular-globals/angular-globals";
import { ChatAiUiService, ChatLocationMode } from "@common/lib/chat-ai/chat-ai-ui.service";
import { DefaultBackFinishButtons, DefaultBackNextButtons, DefaultBackTourButton, DefaultCancelProceedButtons, DefaultNextButtons, DefaultOKButtons, GuidedTour, GuidedTourStepOptions } from "@common/lib/guided-tour/guided-tour.service";
import { GuidedTourUtils } from "@common/lib/guided-tour/guided-tour.utils";
import { PromiseUtilities } from "@common/lib/utilities/promise-utilities";
import { CommonSidebarConstants } from "@common/shell/common-sidebar/common-sidebar.constants";
import { ShellUiService } from "@common/shell/shell-ui.service";
import { ConfigurationTabIcon } from "@org-common/lib/configuration/configuration-tab-content/configuration-tab";
import { SearchTabIcon } from "@org-common/lib/search/search-tab-content/search-tab";
import { TeamTabId } from "@org-common/lib/shell/team-tab-content/team-tab-id";

interface IDashboard {
    name: string;
    text: string;
    icon: string;
}

interface ITab {
    name: string;
    text: string;
    tabId: string;
    tabContentSelector: string;
    expandTreeItem?: boolean;
}

const AiChatFeatureEnabled = () => GuidedTourUtils.isElementVisible(GuidedTourUtils.getFirstElementWithTextContent("adapt-chat-ai-application-bar-item"));
let startAiTourOnSidebar = false;
const MoveToDialogButtonVisible = () => GuidedTourUtils.isElementVisible(GuidedTourUtils.getFirstElementWithTextContent(`[data-test="move-to-dialog-button"]`));
const AiIntroSteps: GuidedTourStepOptions[] = [
    {
        title: "Your AI assistant",
        text: `<p>The <b>Lumia Assistant</b> is available through this button.
            It lets you explore backend data and interact with the current page's content.</p>`,
        showOn: AiChatFeatureEnabled,
        beforeShowPromise: async () => {
            const chatUiService = AngularGlobals.injector.get(ChatAiUiService);
            startAiTourOnSidebar = chatUiService.chatLocationMode === ChatLocationMode.Sidebar;
        },
        attachTo: {
            on: "bottom",
            element: "adapt-chat-ai-application-bar-item",
        },
        canClickTarget: false,
        modalOverlayOpeningPadding: 3,
        buttons: DefaultBackNextButtons,
    },
    {
        title: "Another way to access Lumia Assistant",
        text: `<p>You can also access the <b>Lumia Assistant</b> from here.</p>
            <p>Click here to try out the <b>Lumia Assistant</b>.</p>`,
        showOn: () => AiChatFeatureEnabled() && !MoveToDialogButtonVisible(),
        waitForAndAttachToElementSelector: `[id="chat-ai-sidebar-tab"]`,
        attachTo: {
            on: "right",
        },
        modalOverlayOpeningPadding: 3,
        advanceOn: {
            selector: `[id="chat-ai-sidebar-tab"]`,
            event: "click",
        },
        buttons: [DefaultBackTourButton],
    },
    {
        title: "Another way to access Lumia Assistant",
        text: `<p>You can also access the <b>Lumia Assistant</b> from here.</p>`,
        showOn: () => AiChatFeatureEnabled() && MoveToDialogButtonVisible(),
        waitForAndAttachToElementSelector: `[id="chat-ai-sidebar-tab"]`,
        attachTo: {
            on: "right",
        },
        modalOverlayOpeningPadding: 3,
        buttons: DefaultBackNextButtons,
    },
    {
        title: "Switching between sidebar and dialog",
        text: `<p><b>Click</b> this button to switch between the sidebar and dialog view of the <b>Lumia Assistant</b>.</p>`,
        showOn: () => AiChatFeatureEnabled() && startAiTourOnSidebar,
        waitForAndAttachToElementSelector: `[data-test="move-to-dialog-button"]`,
        attachTo: {
            on: "bottom",
        },
        modalOverlayOpeningPadding: 3,
        advanceOn: {
            selector: `[data-test="move-to-dialog-button"]`,
            event: "click",
        },
        buttons: [DefaultBackTourButton],
    },
    {
        title: "Persistent dialog for seamless navigation",
        text: `<p>This dialog will remain open as you navigate through different pages in the platform, allowing you to interact with the <b>Lumia Assistant</b> without losing context.</p>`,
        showOn: AiChatFeatureEnabled,
        waitForAndAttachToElementSelector: `.adapt-dialog>.dx-overlay-content`,
        attachTo: {
            on: "left",
        },
        modalOverlayOpeningPadding: 0,
        buttons: DefaultNextButtons,
    },
    {
        title: "Switching to sidebar view",
        text: `<p>Click this button to switch to the sidebar view of the <b>Lumia Assistant</b>.</p>`,
        showOn: AiChatFeatureEnabled,
        waitForAndAttachToElementSelector: `[data-test="move-to-sidebar-button"]`,
        attachTo: {
            on: "left",
        },
        modalOverlayOpeningPadding: 3,
        advanceOn: {
            selector: `[data-test="move-to-sidebar-button"]`,
            event: "click",
        },
        buttons: [DefaultBackTourButton],
    },
    {
        title: "Switching back to dialog view",
        text: `<p>Click this button to switch back to the dialog view of the <b>Lumia Assistant</b>.</p>`,
        showOn: () => AiChatFeatureEnabled() && !startAiTourOnSidebar,
        waitForAndAttachToElementSelector: `[data-test="move-to-dialog-button"]`,
        attachTo: {
            on: "bottom",
        },
        modalOverlayOpeningPadding: 3,
        advanceOn: {
            selector: `[data-test="move-to-dialog-button"]`,
            event: "click",
        },
    },
    {
        title: "Start a conversation with Lumia Assistant",
        text: `<p>Start chatting with the <b>Lumia Assistant</b> by typing your message here!</p>`,
        showOn: AiChatFeatureEnabled,
        waitForAndAttachToElementSelector: ".dx-chat-messagebox-textarea-container",
        attachTo: {
            on: "top",
        },
        modalOverlayOpeningPadding: 3,
        canClickTarget: false,
        buttons: DefaultNextButtons,
    },
    {
        title: "Easily attach files in your message",
        text: `<p>You can attach files to your message by clicking this button.</p>`,
        showOn: () => AiChatFeatureEnabled() && GuidedTourUtils.isElementVisible(GuidedTourUtils.getFirstElementWithTextContent(".dx-chat-textarea-attach-button")),
        waitForAndAttachToElementSelector: ".dx-chat-textarea-attach-button",
        attachTo: {
            on: "top",
        },
        modalOverlayOpeningPadding: 3,
        canClickTarget: false,
        buttons: DefaultBackNextButtons,
    },
    {
        title: "Wrapping up the Lumia Assistant tour",
        text: `<p>That's the end of the tour for the <b>Lumia Assistant</b>!
            Feel free to explore and interact with it to see how it can assist you.</p>
            <p>To continue the tour, close this dialog by clicking here.</p>`,
        showOn: () => AiChatFeatureEnabled() && !startAiTourOnSidebar,
        waitForAndAttachToElementSelector: `[data-test="dialog-close-button"]`,
        attachTo: {
            on: "left",
        },
        advanceOn: {
            selector: `[data-test="dialog-close-button"]`,
            event: "click",
        },
        modalOverlayOpeningPadding: 3,
        buttons: [DefaultBackTourButton],
    },
];

const AiIntroFinalSteps: GuidedTourStepOptions[] = [
    {
        title: "Wrapping up the Lumia Assistant tour",
        text: `<p>That's the end of the tour for the <b>Lumia Assistant</b>!
            Feel free to explore and interact with it to see how it can assist you.</p>
            <p>This dialog can be closed by clicking here.</p>`,
        showOn: () => AiChatFeatureEnabled() && !startAiTourOnSidebar,
        waitForAndAttachToElementSelector: `[data-test="dialog-close-button"]`,
        attachTo: {
            on: "left",
        },
        advanceOn: {
            selector: `[data-test="dialog-close-button"]`,
            event: "click",
        },
        modalOverlayOpeningPadding: 3,
        buttons: DefaultBackFinishButtons,
    },
    {
        title: "Wrapping up the Lumia Assistant tour",
        text: `<p>That's the end of the tour for the <b>Lumia Assistant</b>!
            Feel free to explore and interact with it to see how it can assist you.</p>`,
        showOn: () => AiChatFeatureEnabled() && startAiTourOnSidebar,
        buttons: DefaultBackFinishButtons,
    },
];

const tabs: ITab[] = [
    {
        name: "Personal tab",
        text: `<p>The personal tab shows information relevant to you in this organisation. This includes:</p>
        <ul class="list-content">
            <li>Actions assigned to you</li>
            <li>Your career valuations</li>
            <li>Your personal profile page</li>
            <li>and more...</li>
        </ul>
        <p>You can click on your name to view your personal dashboard.</p>`,
        tabId: CommonSidebarConstants.PersonSidebarId,
        tabContentSelector: "adapt-personal-tab-content",
    },
    {
        name: "Teams tab",
        text: `<p>This navigation tab is the home of all the teams in the organisation. Typically, the following is viewable for a team:</p>
        <ul class="list-content">
            <li>Systems</li>
            <li>Objectives</li>
            <li>Actions</li>
            <li>Meetings</li>
            <li>and more...</li>
        </ul>
        <p>Clicking on a team name will take you to the team dashboard.
            This allows you to see the team's roles, values, guidance, and more at a glance.</p>
        <p>You can click on "All teams" to view the organisation map, which gives an overview of the teams within the organisation in their respective zones.</p>`,
        tabId: TeamTabId,
        tabContentSelector: "adapt-team-tab-content",
        expandTreeItem: true,
    },
    {
        name: "Organisation tab",
        text: `<p>This navigation tab is the home of everything relevant to the entire organisation.</p>
        <p>You can click on the organisation name to view the organisation dashboard.
            This allows you to see the organisation's purpose, visions, values and more at a glance.</p>`,
        tabId: CommonSidebarConstants.OrganisationSidebarId,
        tabContentSelector: "adapt-organisation-tab-content",
    },
];

const actions: IDashboard[] = [
    {
        name: "Search",
        text: "search for anything inside the Lumia platform, such as people, teams, roles, work, objectives or support articles.",
        icon: SearchTabIcon,
    },
    {
        name: "Configuration",
        text: "configure your account security options, and your personal action boards (if you have access to actions).",
        icon: ConfigurationTabIcon,
    },
    {
        name: "Help",
        text: "view support articles, help & documentation for the Lumia platform.",
        icon: "fal fa-fw fa-circle-question",
    },
];
const actionsWithAiChat: IDashboard[] = [
    {
        name: "Lumia Assistant",
        text: "chat with the Lumia Assistant, your AI companion within the Lumia platform. It can assist you with navigating the platform, finding information, and answering any questions you have about using Lumia.",
        icon: "fal fa-fw fa-stars",
    },
    ...actions,
];

const actionsText = actions.map((action) => {
    return `<p><b><i class="${action.icon}"></i> ${action.name}</b>: ${action.text}</p>`;
}).join("\n");
const actionsTextWithAiChat = actionsWithAiChat.map((action) => {
    return `<p><b><i class="${action.icon}"></i> ${action.name}</b>: ${action.text}</p>`;
}).join("\n");

const TakeTourAgainStep: GuidedTourStepOptions = {
    title: "Take the tour again",
    text: "You can take this tour again by clicking this button.",
    waitForAndAttachToElementSelector: `[data-tour="personal-dashboard-tour"]`,
    attachTo: {
        on: "bottom-start",
    },
    modalOverlayOpeningPadding: 3,
    buttons: [DefaultBackTourButton, DefaultOKButtons[0]],
};

export const EmbedShellTourButton: GuidedTour = {
    steps: [{
        ...TakeTourAgainStep,
        title: "Take the tour later.",
        text: "Take the tour anytime by clicking this button.",
        buttons: DefaultOKButtons,
    }],
};

export const EmbedShellTour: GuidedTour = {
    steps: [...(tabs.map((tab, index) => ({
        title: tab.name,
        text: tab.text,
        // waitForAndAttachToElementSelector: tab.tabContentSelector,
        beforeShowPromise: async () => {
            const shellUiService = AngularGlobals.injector.get(ShellUiService);
            shellUiService.focusTab(tab.tabId);
            await GuidedTourUtils.waitForElementWithTextContentToBeVisible(tab.tabContentSelector);

            if (tab.expandTreeItem) {
                const teamNodes = Array.from(document.querySelectorAll<HTMLLIElement>("adapt-team-tab-content .dx-treeview-node"));
                if (teamNodes.length > 0 && teamNodes.every((node) => node.ariaExpanded === "false")) {
                    // no expanded team node, but there are some that exist, so expand the first one.
                    teamNodes[0].querySelector<HTMLDivElement>("div.dx-treeview-toggle-item-visibility")?.click();
                    await PromiseUtilities.wait(500);
                }
            }
        },
        attachTo: { on: "right", element: tab.tabContentSelector },
        modalOverlayOpeningPadding: 72,
        modalOverlayOpeningXOffset: -52,
        canClickTarget: false,
        buttons: index === 0 ? DefaultCancelProceedButtons : DefaultBackNextButtons,
        showOn: () => !!document.getElementById(tab.tabId),
    } as GuidedTourStepOptions))), {
        title: "Platform actions",
        text: `<p>Clicking on these will let you do the following:</p>
        ${actionsText}`,
        waitForAndAttachToElementSelector: ".vertical-tabs-bottom",
        attachTo: {
            on: "right",
        },
        showOn: () => !AiChatFeatureEnabled(),
        modalOverlayOpeningPadding: 3,
        canClickTarget: false,
        buttons: DefaultBackNextButtons,
    }, {
        title: "Platform actions",
        text: `<p>Clicking on these will let you do the following:</p>
        ${actionsTextWithAiChat}`,
        waitForAndAttachToElementSelector: ".vertical-tabs-bottom",
        attachTo: {
            on: "right",
        },
        showOn: AiChatFeatureEnabled,
        modalOverlayOpeningPadding: 3,
        canClickTarget: false,
        buttons: DefaultBackNextButtons,
    }, {
        title: "Sidebar Show/Hide",
        text: `<p>To give you more space, you can show or hide the sidebar.</p>`,
        waitForAndAttachToElementSelector: `[data-tour="sidebar-toggle"]`,
        attachTo: {
            on: "right",
        },
        modalOverlayOpeningPadding: 3,
        buttons: DefaultBackNextButtons,
    }, {
        title: "Navigation bar",
        text: `<p>This is the navigation bar. It shows the following:</p>
        <b>Organisation logo</b>:
            on the left is your organisation's logo. This can be updated at any time through the organisation configuration.<br/><br/>
        <b>Navigation breadcrumbs</b>:
            next to your logo are the page breadcrumbs. You can click these at any time for easy navigation.<br/><br/>
        <b>Search bar</b>:
            first on the right is the search bar. You can quickly search for people, teams and roles, or press enter to perform a full search.<br/><br/>
        <b>Recent activities</b>:
            next to the search bar is your recent activities. This will notify you of changes to anything relevant to you, such as your actions or objectives.<br/><br/>
        <b>Your profile</b>:
            on the right is your profile. From there, you can edit your profile (such as your name & photo), or simply logout from the platform.`,
        waitForAndAttachToElementSelector: ".navbar",
        attachTo: {
            on: "bottom",
        },
        canClickTarget: false,
        buttons: DefaultBackNextButtons,
    }, {
        // this step will only be shown if intercom button is there
        title: "Chat with us",
        text: "You can chat with us if you have any issues or need help with the platform - this is available by clicking this icon.",
        waitForAndAttachToElementSelector: [".intercom-launcher-frame", ".intercom-launcher"],
        attachTo: {
            on: "top",
        },
        showOn: () => isIntercomVisible(),
        canClickTarget: false,
        buttons: DefaultBackNextButtons,
    }, ...AiIntroSteps, {
        // this step will only be shown if intercom button is not there
        title: "Chat with us",
        text: "You can chat with us if you have any issues or need help with the platform - this is available by clicking the speech bubble in the bottom right corner.",
        showOn: () => !isIntercomVisible(),
        buttons: DefaultBackNextButtons,
    }, {
        ...TakeTourAgainStep,
        buttons: DefaultBackFinishButtons,
    }],
};

export const AiTour: GuidedTour = {
    steps: [...AiIntroSteps.slice(0, -1), ...AiIntroFinalSteps],
};


function isIntercomVisible() {
    const intercomFrame = GuidedTourUtils.getFirstElementWithTextContent(".intercom-launcher-frame");
    const intercomLauncher = GuidedTourUtils.getFirstElementWithTextContent(".intercom-launcher");
    return GuidedTourUtils.isElementVisible(intercomFrame) || GuidedTourUtils.isElementVisible(intercomLauncher);
}
